import {createRef, RefObject, useEffect, useMemo, useState} from 'react';

import {AnswerValue, LeadFormBlockQuestionAnswer, BlsFormState, BlsFormQuestion} from '@target-web/leadads-form-view';

import useAnalytics from './useAnalytics';

export interface LeadFormControllerProps {
    questions: BlsFormQuestion[];
}

function useBlsFormController({questions}: LeadFormControllerProps) {
    const {leadAnswerAnalyticEvent} = useAnalytics();

    const [formState, setFormState] = useState<BlsFormState>({
        answers: {},
        emptyAnswersIds: [],
        answersRefs:
            questions.reduce<Record<string, RefObject<HTMLDivElement>>>((res, cur) => {
                res[cur.block_id] = createRef<HTMLDivElement>();
                return res;
            }, {}) || {},
    });

    //  Не добавлено в formState, так как это чисто private переменная для облегчения поиска вариантов
    //  ответов с дополнительными условиями
    const pageAnswers = useMemo(() => {
        return (
            questions.reduce<Record<string, LeadFormBlockQuestionAnswer>>((res, cur) => {
                const {type} = cur;
                if (type !== 'one_answer' && type !== 'multiple_answers' && type !== 'select_answer') return res;
                cur.answers.forEach((answer) => {
                    res[answer.id] = answer;
                });
                return res;
            }, {}) || {}
        );
    }, [questions]);

    const validate = () => {
        const emptyAnswersIds = Object.keys(formState.answers)
            .map((id) => {
                const value = formState.answers[id].value;
                const needAnswerText = value.answer_ids?.length
                    ? value.answer_ids.reduce((acc, cur) => acc || pageAnswers[cur].type === 4, false) &&
                      !value.answer_text?.trim().length
                    : false;
                const isFilled = value?.answer_ids?.length
                    ? value.answer_ids.length > 0 && !needAnswerText
                    : !!value?.answer_text?.trim() || value.answer_scale !== undefined;

                return isFilled ? undefined : id;
            })
            .filter(Boolean) as string[];

        setFormState((prevState) => ({
            ...prevState,
            emptyAnswersIds,
        }));

        return {
            isValid: !emptyAnswersIds.length,
            emptyAnswersIds,
        };
    };

    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            answers:
                questions.reduce((acc, {block_id, page_id}) => {
                    acc[block_id] = {
                        page_id, // храним тут, чтобы потом передать в запрос лида
                        value: {},
                        onChange: (value: AnswerValue) => {
                            setFormState((prevState) => {
                                let emptyAnswersIds = prevState.emptyAnswersIds;
                                if (value.answer_ids?.length || !!value.answer_text) {
                                    emptyAnswersIds = emptyAnswersIds.filter((id) => id !== block_id);
                                }

                                const answ = prevState.answers[block_id];
                                if (answ) {
                                    return {
                                        ...prevState,
                                        emptyAnswersIds,
                                        answers: {
                                            ...prevState.answers,
                                            [block_id]: {
                                                ...answ,
                                                value,
                                            },
                                        },
                                    };
                                }
                                return prevState;
                            });
                        },
                        onAnalyticEvent: () => leadAnswerAnalyticEvent({block_id}),
                    };

                    return acc;
                }, {} as BlsFormState['answers']) || {},
            emptyAnswersIds: [],
            answersRefs: formState.answersRefs,
        }));
    }, [formState.answersRefs, leadAnswerAnalyticEvent, questions]);

    return {formState, validate};
}

export default useBlsFormController;
